import { getItemDef } from "../items/db.js";
import { STASH_H, STASH_W } from "../meta/profile.js";
import type { SkillId } from "../meta/progress.js";
import type { CraftDef, HideoutModuleDef } from "./types.js";

/**
 * The cellar under the Gasthaus, as content: nine modules, eight recipes.
 *
 * LAGER pays off immediately (a bigger stash) and gates the two flavour
 * modules. GENERATOR turns fuel into the right to run the recipes that matter
 * and gates both level-2 crafting modules; level 2 halves the burn. WERKBANK
 * and SANITAET eat barter goods and give back cartridges and bandages — loot
 * that had one use acquires a second. BRENNEREI and BIENENSTOCK close a small
 * loop (honey → schnapps → medical corner) and need no power. SCHIESSSTAND,
 * KRAFTRAUM and FUNKRAUM are the walkable shelter's rooms: pure practice, a
 * SKILL on a cooldown, and an xp multiplier on everything earned elsewhere.
 *
 * Each level's `blurb` is the UI copy; the mechanical effect is whichever of
 * `stashSizeFor`, `generatorBurnPerSec`, `productionFor`, `xpMultiplierFor`
 * and `trainingFor` names the module. Change one, change the other —
 * `validateHideout` cannot catch a stale blurb.
 */

// ---------------------------------------------------------------------------
// Fuel
// ---------------------------------------------------------------------------

/** The item the generator eats. One canister, one `hideoutFuel` message. */
export const FUEL_DEF_ID = "benzinkanister";

/** Abstract fuel units one canister adds to the tank. */
export const FUEL_UNITS_PER_CANISTER = 20;

/**
 * Tank capacity — three canisters, an hour of powered crafting at level 1 and
 * two at level 2. A cap so the generator is topped up between raids, not filled
 * once.
 */
export const FUEL_TANK_CAP = 60;

/** The module whose level decides burn rate and whether anything is powered. */
export const GENERATOR_MODULE_ID = "generator";
/** The module whose level decides the stash grid's size. */
export const LAGER_MODULE_ID = "lager";
/** The only passive producer in v1. */
export const BIENENSTOCK_MODULE_ID = "bienenstock";
/**
 * The shooting range: no effect function to look up — its level decides plates
 * and drill in the room's own code.
 */
export const RANGE_MODULE_ID = "schiessstand";
/** The gym — the only module that pays a SKILL rather than an item, a rate or
 *  a grid. See `trainingFor`. */
export const GYM_MODULE_ID = "kraftraum";
/** The radio room, whose whole effect is `xpMultiplierFor`. */
export const INTEL_MODULE_ID = "funkraum";

/**
 * Fuel units burned per second, indexed by generator level (0 = no generator).
 * Level 1 empties a canister in twenty minutes, level 2 in forty.
 */
const GENERATOR_BURN_PER_SEC: readonly number[] = [0, 1 / 60, 0.5 / 60];

/**
 * Tank drain at `level`, fuel units per second; 0 at level 0. Out-of-range
 * levels answer with the nearest real rate — the level may come off a database
 * blob.
 */
export function generatorBurnPerSec(level: number): number {
  if (!Number.isFinite(level) || level <= 0) return 0;
  const i = Math.floor(level);
  const last = GENERATOR_BURN_PER_SEC.length - 1;
  return GENERATOR_BURN_PER_SEC[i > last ? last : i] ?? 0;
}

// ---------------------------------------------------------------------------
// Module effects
// ---------------------------------------------------------------------------

/**
 * Stash heights by Lagerregal level. Width never changes: rows appended at the
 * bottom leave every item where the player put it.
 */
const STASH_HEIGHT_BY_LEVEL: readonly number[] = [STASH_H, 22, 28];

/**
 * The stash grid's size at a Lagerregal level. Level 0 is `STASH_W` × `STASH_H`
 * from `meta/profile.ts`, so "not built" and "does not exist" are the same
 * stash.
 */
export function stashSizeFor(lagerLevel: number): { w: number; h: number } {
  const last = STASH_HEIGHT_BY_LEVEL.length - 1;
  let i = 0;
  if (Number.isFinite(lagerLevel) && lagerLevel > 0) {
    i = Math.floor(lagerLevel);
    if (i > last) i = last;
  }
  return { w: STASH_W, h: STASH_HEIGHT_BY_LEVEL[i] ?? STASH_H };
}

/** What a passive producer makes, how often, and how much of it fits in the
 *  buffer before the module stops working. */
export interface ProductionDef {
  defId: string;
  periodSec: number;
  cap: number;
}

const HIVE_LEVEL_1: ProductionDef = { defId: "honigglas", periodSec: 600, cap: 2 };
const HIVE_LEVEL_2: ProductionDef = { defId: "honigglas", periodSec: 400, cap: 3 };

/**
 * What `moduleId` produces on its own at `level`, or `null` (every module but
 * the hive). The cap freezes a full buffer, so harvesting is a visit rather
 * than a number that only goes up.
 */
export function productionFor(moduleId: string, level: number): ProductionDef | null {
  if (moduleId !== BIENENSTOCK_MODULE_ID) return null;
  if (!Number.isFinite(level)) return null;
  const l = Math.floor(level);
  if (l <= 0) return null;
  return l === 1 ? HIVE_LEVEL_1 : HIVE_LEVEL_2;
}

/**
 * Multiplier on every experience payment, indexed by Funkraum level. Index 0 is
 * 1, so callers fold it in unconditionally.
 */
const XP_MULT_BY_LEVEL: readonly number[] = [1, 1.1, 1.2];

/**
 * The radio room's xp multiplier at `intelLevel`. Clamped like
 * `generatorBurnPerSec` (the level may come off a blob) and never below 1,
 * which `validateHideout` pins.
 */
export function xpMultiplierFor(intelLevel: number): number {
  if (!Number.isFinite(intelLevel) || intelLevel <= 0) return 1;
  const i = Math.floor(intelLevel);
  const last = XP_MULT_BY_LEVEL.length - 1;
  return XP_MULT_BY_LEVEL[i > last ? last : i] ?? 1;
}

/** What one gym session is worth, and how long before the next one. */
export interface TrainingDef {
  /** Skill points a PERFECT session pays, before the diminishing-returns
   *  brake in `trainSkill` takes its share. */
  gainPoints: number;
  /** Wall-clock seconds between one session and the next. */
  cooldownSec: number;
}

const GYM_LEVEL_1: TrainingDef = { gainPoints: 1.5, cooldownSec: 1800 };
const GYM_LEVEL_2: TrainingDef = { gainPoints: 2.25, cooldownSec: 1200 };

/**
 * What a gym session pays at `level`, or `null` with no gym. The cooldown is
 * the only budget — no euros, items or fuel — so the server stamps
 * `HideoutState.training` with its own clock.
 */
export function trainingFor(gymLevel: number): TrainingDef | null {
  if (!Number.isFinite(gymLevel)) return null;
  const l = Math.floor(gymLevel);
  if (l <= 0) return null;
  return l === 1 ? GYM_LEVEL_1 : GYM_LEVEL_2;
}

/**
 * What one finished session pays for `score01` ∈ [0, 1]: half the session at 0,
 * all of it at 1. Total and clamped — the score arrives over the wire
 * (`hideoutTrain`).
 */
export function trainingGain(gymLevel: number, score01: number): number {
  const def = trainingFor(gymLevel);
  if (def === null) return 0;
  return def.gainPoints * (0.5 + 0.5 * clamp01(score01));
}

/** [0, 1], with anything that is not a real number reading as 0. */
function clamp01(x: number): number {
  if (!Number.isFinite(x) || x <= 0) return 0;
  return x > 1 ? 1 : x;
}

/**
 * The skills the gym can teach — the two a bench and a bar can explain.
 * `SkillId` is imported type-only so progression never names a module.
 */
export const TRAINABLE_SKILLS: readonly SkillId[] = ["endurance", "strength"];

/** Whether a string off the wire names a skill the gym can teach. The type
 *  guard the server's `hideoutTrain` handler narrows with. */
export function isTrainableSkill(x: string): x is SkillId {
  return (TRAINABLE_SKILLS as readonly string[]).includes(x);
}

// ---------------------------------------------------------------------------
// The modules
// ---------------------------------------------------------------------------

/** Every module, in DISPLAY ORDER — this is the order the hideout screen lists
 *  them in, and the order `advanceHideout` walks production in. */
export const HIDEOUT_MODULES: readonly HideoutModuleDef[] = [
  {
    id: LAGER_MODULE_ID,
    name: "Lagerregal",
    blurb: "Shelving along the cellar wall. Every board is another row of stash.",
    levels: [
      {
        euros: 120,
        items: [
          { defId: "naegel", count: 3 },
          { defId: "schraubenzieher", count: 1 },
        ],
        requires: [],
        buildSec: 60,
        blurb: "stash grows to 10×22",
      },
      {
        euros: 400,
        items: [
          { defId: "naegel", count: 6 },
          { defId: "schraubenzieher", count: 2 },
          { defId: "kupferdraht", count: 2 },
        ],
        requires: [],
        buildSec: 300,
        blurb: "stash grows to 10×28",
      },
    ],
  },
  {
    id: GENERATOR_MODULE_ID,
    name: "Notstromaggregat",
    blurb: "A farm generator bolted to the floor. Loud, thirsty, and the reason anything else runs.",
    levels: [
      {
        euros: 150,
        items: [
          { defId: "zuendkerzen", count: 2 },
          { defId: "batterie", count: 1 },
          { defId: "kupferdraht", count: 1 },
        ],
        requires: [],
        buildSec: 90,
        blurb: "power for the workbench and the medical corner",
      },
      {
        euros: 500,
        items: [
          { defId: "zuendkerzen", count: 4 },
          { defId: "batterie", count: 2 },
          { defId: "kupferdraht", count: 2 },
          { defId: "schraubenzieher", count: 1 },
        ],
        requires: [],
        buildSec: 420,
        blurb: "burns half the fuel",
      },
    ],
  },
  {
    id: "werkbank",
    name: "Werkbank",
    blurb: "A vice, a press and a tin of primers. Cartridges come out of it faster than money does.",
    levels: [
      {
        euros: 80,
        items: [
          { defId: "naegel", count: 2 },
          { defId: "schraubenzieher", count: 1 },
        ],
        requires: [],
        buildSec: 120,
        blurb: "loads buckshot and 9×19",
      },
      {
        euros: 350,
        items: [
          { defId: "kupferdraht", count: 2 },
          { defId: "zuendkerzen", count: 2 },
          { defId: "batterie", count: 1 },
        ],
        requires: [{ moduleId: GENERATOR_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "loads 8×57, and turns out foregrips",
      },
    ],
  },
  {
    id: "sanitaet",
    name: "Sanitätsecke",
    blurb: "A folding table, a lamp and the inn's whole supply of brandy. Wounds get dressed here.",
    levels: [
      {
        euros: 100,
        items: [
          { defId: "med_verband", count: 2 },
          { defId: "schnaps", count: 1 },
        ],
        requires: [],
        buildSec: 120,
        blurb: "rolls dressings and first aid pouches",
      },
      {
        euros: 400,
        items: [
          { defId: "med_schiene", count: 2 },
          { defId: "schnaps", count: 2 },
          { defId: "med_erstehilfe", count: 1 },
        ],
        requires: [{ moduleId: GENERATOR_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "assembles the full medical kit",
      },
    ],
  },
  {
    id: "brennerei",
    name: "Schnapsbrennerei",
    blurb: "A copper still on a gas ring. Illegal before the lockdown and nobody's business now.",
    levels: [
      {
        euros: 250,
        items: [
          { defId: "kupferdraht", count: 2 },
          { defId: "feuerzeug", count: 1 },
          { defId: "honigglas", count: 2 },
        ],
        requires: [{ moduleId: LAGER_MODULE_ID, level: 1 }],
        buildSec: 300,
        blurb: "turns honey into brandy, no power needed",
      },
    ],
  },
  {
    id: BIENENSTOCK_MODULE_ID,
    name: "Bienenstock",
    blurb: "Two hives behind the woodshed. They work whether or not you come home.",
    levels: [
      {
        euros: 200,
        items: [
          { defId: "naegel", count: 4 },
          { defId: "honigglas", count: 1 },
        ],
        requires: [{ moduleId: LAGER_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "a jar of honey every 10 minutes, two in the buffer",
      },
      {
        euros: 450,
        items: [
          { defId: "naegel", count: 6 },
          { defId: "honigglas", count: 2 },
        ],
        requires: [],
        buildSec: 900,
        blurb: "a jar every 400 seconds, and room for a third",
      },
    ],
  },
  {
    id: RANGE_MODULE_ID,
    name: "Schießstand",
    blurb: "A collapsed service tunnel behind the east wall, dug out and lined with sandbags.",
    levels: [
      {
        euros: 300,
        items: [
          { defId: "naegel", count: 4 },
          { defId: "schraubenzieher", count: 1 },
          { defId: "kupferdraht", count: 1 },
        ],
        requires: [{ moduleId: LAGER_MODULE_ID, level: 1 }],
        buildSec: 300,
        blurb:
          "clears the tunnel: three steel plates at 10, 20 and 30 m, and all the practice ammunition you can burn",
      },
      {
        euros: 700,
        items: [
          { defId: "naegel", count: 6 },
          { defId: "batterie", count: 1 },
          { defId: "zuendkerzen", count: 1 },
        ],
        requires: [{ moduleId: GENERATOR_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "five plates, and a timed drill that lights them up one by one",
      },
    ],
  },
  {
    id: GYM_MODULE_ID,
    name: "Kraftraum",
    blurb: "A bench, a bar and whatever weighs something.",
    levels: [
      {
        euros: 250,
        items: [
          { defId: "naegel", count: 4 },
          { defId: "kupferdraht", count: 1 },
        ],
        requires: [{ moduleId: LAGER_MODULE_ID, level: 1 }],
        buildSec: 300,
        blurb: "a bench and a bar: a training session raises Endurance or Strength, once every 30 minutes",
      },
      {
        euros: 600,
        items: [
          { defId: "naegel", count: 4 },
          { defId: "batterie", count: 1 },
          { defId: "schraubenzieher", count: 1 },
        ],
        requires: [{ moduleId: GENERATOR_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "half again as much per session, and only 20 minutes between them",
      },
    ],
  },
  {
    id: INTEL_MODULE_ID,
    name: "Funkraum",
    blurb: "A radio set, a wall of maps and a kettle.",
    levels: [
      {
        euros: 500,
        items: [
          { defId: "batterie", count: 2 },
          { defId: "kupferdraht", count: 2 },
          { defId: "zuendkerzen", count: 1 },
        ],
        requires: [{ moduleId: GENERATOR_MODULE_ID, level: 1 }],
        buildSec: 600,
        blurb: "+10 % experience from every raid and every quest",
      },
      {
        euros: 1200,
        items: [
          { defId: "batterie", count: 3 },
          { defId: "kupferdraht", count: 3 },
          { defId: "schraubenzieher", count: 1 },
        ],
        requires: [{ moduleId: LAGER_MODULE_ID, level: 2 }],
        buildSec: 1200,
        blurb: "+20 % experience from every raid and every quest",
      },
    ],
  },
];

/**
 * Registry keyed by id — a `Map`, so `getHideoutModule("toString")` is `null`.
 */
const MODULE_BY_ID: ReadonlyMap<string, HideoutModuleDef> = new Map(
  HIDEOUT_MODULES.map((m) => [m.id, m]),
);

/** Non-throwing lookup. `null` for anything unknown. */
export function getHideoutModule(id: string): HideoutModuleDef | null {
  return MODULE_BY_ID.get(id) ?? null;
}

// ---------------------------------------------------------------------------
// The recipes
// ---------------------------------------------------------------------------

/**
 * Every recipe, grouped by module. Balance rule: output worth more than inputs
 * at trader prices, but only just; the ammunition recipes are the generous
 * ones.
 */
export const HIDEOUT_CRAFTS: readonly CraftDef[] = [
  {
    id: "craft_buckshot",
    moduleId: "werkbank",
    minLevel: 1,
    needsPower: true,
    inputs: [
      { defId: "naegel", count: 2 },
      { defId: "zuendkerzen", count: 1 },
    ],
    output: { defId: "ammo_12g_buck", count: 20 },
    craftSec: 300,
  },
  {
    id: "craft_9mm",
    moduleId: "werkbank",
    minLevel: 1,
    needsPower: true,
    inputs: [
      { defId: "kupferdraht", count: 1 },
      { defId: "batterie", count: 1 },
    ],
    output: { defId: "ammo_9x19_fmj", count: 50 },
    craftSec: 360,
  },
  {
    id: "craft_8x57",
    moduleId: "werkbank",
    minLevel: 2,
    needsPower: true,
    inputs: [
      { defId: "zuendkerzen", count: 2 },
      { defId: "kupferdraht", count: 1 },
      { defId: "batterie", count: 1 },
    ],
    output: { defId: "ammo_8x57_fmj", count: 30 },
    craftSec: 600,
  },
  {
    id: "craft_foregrip",
    moduleId: "werkbank",
    minLevel: 2,
    needsPower: true,
    inputs: [
      { defId: "naegel", count: 2 },
      { defId: "schraubenzieher", count: 1 },
    ],
    output: { defId: "foregrip", count: 1 },
    craftSec: 480,
  },
  {
    id: "craft_verband",
    moduleId: "sanitaet",
    minLevel: 1,
    // The one recipe a player with no generator can still run, and the reason
    // the medical corner is worth building first.
    needsPower: false,
    inputs: [
      { defId: "schnaps", count: 1 },
      { defId: "streichhoelzer", count: 1 },
    ],
    output: { defId: "med_verband", count: 2 },
    craftSec: 240,
  },
  {
    id: "craft_erstehilfe",
    moduleId: "sanitaet",
    minLevel: 1,
    needsPower: true,
    inputs: [
      { defId: "med_verband", count: 2 },
      { defId: "schnaps", count: 1 },
    ],
    output: { defId: "med_erstehilfe", count: 1 },
    craftSec: 420,
  },
  {
    id: "craft_sanikasten",
    moduleId: "sanitaet",
    minLevel: 2,
    needsPower: true,
    inputs: [
      { defId: "med_erstehilfe", count: 1 },
      { defId: "med_verband", count: 2 },
      { defId: "med_schiene", count: 1 },
    ],
    output: { defId: "med_sanikasten", count: 1 },
    craftSec: 900,
  },
  {
    id: "craft_schnaps",
    moduleId: "brennerei",
    minLevel: 1,
    needsPower: false,
    inputs: [
      { defId: "honigglas", count: 2 },
      { defId: "streichhoelzer", count: 1 },
    ],
    output: { defId: "schnaps", count: 2 },
    craftSec: 600,
  },
];

/** Registry keyed by id, a `Map` for the same reason `MODULE_BY_ID` is one. */
const CRAFT_BY_ID: ReadonlyMap<string, CraftDef> = new Map(HIDEOUT_CRAFTS.map((c) => [c.id, c]));

/** Non-throwing lookup. `null` for anything unknown. */
export function getCraft(id: string): CraftDef | null {
  return CRAFT_BY_ID.get(id) ?? null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Every cost/input/output count must be a whole number of whole things. */
function checkCosts(
  problems: string[],
  where: string,
  costs: readonly { defId: string; count: number }[],
): void {
  const seen = new Set<string>();
  for (const c of costs) {
    if (seen.has(c.defId)) problems.push(`${where} names "${c.defId}" twice`);
    seen.add(c.defId);
    if (getItemDef(c.defId) === null) {
      problems.push(`${where} names unknown item "${c.defId}"`);
      continue;
    }
    // Cash is charged as `HideoutLevelDef.euros`, never as an item cost.
    if (c.defId === "euro") problems.push(`${where} names cash as an item cost`);
    if (!Number.isInteger(c.count) || c.count < 1) {
      problems.push(`${where} wants ${c.count} x "${c.defId}" (must be an integer >= 1)`);
    }
  }
}

/**
 * Structural check of the content above, in the shape of `validateItemDb`: an
 * empty result means the hideout is sound. Runs as a unit test and a boot
 * assertion — every failure here is invisible until a player hits it.
 */
export function validateHideout(): string[] {
  const problems: string[] = [];

  if (getItemDef(FUEL_DEF_ID) === null) problems.push(`fuel item "${FUEL_DEF_ID}" is not in the item DB`);
  if (!Number.isInteger(FUEL_UNITS_PER_CANISTER) || FUEL_UNITS_PER_CANISTER < 1) {
    problems.push(`FUEL_UNITS_PER_CANISTER is ${FUEL_UNITS_PER_CANISTER}`);
  }
  if (FUEL_TANK_CAP < FUEL_UNITS_PER_CANISTER) {
    problems.push("the tank holds less than one canister");
  }

  const seenModules = new Set<string>();
  for (const m of HIDEOUT_MODULES) {
    const where = `hideout module "${m.id}"`;
    if (m.id.length === 0) problems.push("a hideout module has an empty id");
    if (seenModules.has(m.id)) problems.push(`duplicate hideout module id "${m.id}"`);
    seenModules.add(m.id);
    if (MODULE_BY_ID.get(m.id) !== m) problems.push(`${where} is not the one the registry holds`);
    if (m.name.length === 0) problems.push(`${where} has an empty name`);
    if (m.blurb.length === 0) problems.push(`${where} has an empty blurb`);
    if (m.levels.length === 0) problems.push(`${where} has no levels`);

    for (const [i, lvl] of m.levels.entries()) {
      const lw = `${where} level ${i + 1}`;
      if (!Number.isInteger(lvl.euros) || lvl.euros < 0) problems.push(`${lw} costs ${lvl.euros} €`);
      if (!(lvl.buildSec > 0) || !Number.isFinite(lvl.buildSec)) {
        problems.push(`${lw} has buildSec ${lvl.buildSec} (must be > 0)`);
      }
      if (lvl.blurb.length === 0) problems.push(`${lw} has an empty blurb`);
      checkCosts(problems, lw, lvl.items);

      for (const req of lvl.requires) {
        // A module that requires itself can never be built at all: the check
        // reads the level it is about to leave.
        if (req.moduleId === m.id) problems.push(`${lw} requires itself`);
        const target = getHideoutModule(req.moduleId);
        if (target === null) {
          problems.push(`${lw} requires unknown module "${req.moduleId}"`);
          continue;
        }
        if (!Number.isInteger(req.level) || req.level < 1 || req.level > target.levels.length) {
          problems.push(
            `${lw} requires "${req.moduleId}" level ${req.level}, which tops out at ${target.levels.length}`,
          );
        }
      }
    }

    // Production is authored as a function rather than as data, so the only way
    // to check it is to ask it about every level the module actually has.
    for (let level = 1; level <= m.levels.length; level++) {
      const prod = productionFor(m.id, level);
      if (prod === null) continue;
      const pw = `${where} production at level ${level}`;
      if (getItemDef(prod.defId) === null) problems.push(`${pw} makes unknown item "${prod.defId}"`);
      if (!(prod.periodSec > 0) || !Number.isFinite(prod.periodSec)) {
        problems.push(`${pw} has periodSec ${prod.periodSec} (must be > 0)`);
      }
      if (!Number.isInteger(prod.cap) || prod.cap < 1) problems.push(`${pw} has cap ${prod.cap}`);
    }
  }

  // The two effects authored as functions, checked by asking about every level
  // the module has.
  const intel = getHideoutModule(INTEL_MODULE_ID);
  if (intel === null) {
    problems.push(`the intelligence module "${INTEL_MODULE_ID}" is not in the roster`);
  } else {
    // A "bonus" below 1 would silently DELETE experience the player earned, and
    // one that went down with a level would make an upgrade a punishment.
    let prevMult = xpMultiplierFor(0);
    if (prevMult !== 1) problems.push(`xpMultiplierFor(0) is ${prevMult} (must be exactly 1)`);
    for (let level = 1; level <= intel.levels.length; level++) {
      const mult = xpMultiplierFor(level);
      if (!(mult >= 1) || !Number.isFinite(mult)) {
        problems.push(`xpMultiplierFor(${level}) is ${mult} (must be >= 1)`);
      }
      if (mult < prevMult) problems.push(`xpMultiplierFor(${level}) is below level ${level - 1}`);
      prevMult = mult;
    }
  }

  const gym = getHideoutModule(GYM_MODULE_ID);
  if (gym === null) {
    problems.push(`the gym module "${GYM_MODULE_ID}" is not in the roster`);
  } else {
    // Level 0 must be null and every real level must not be: `trainProblem`
    // reads exactly this to tell "there is no gym" from "the bench is free".
    if (trainingFor(0) !== null) problems.push("trainingFor(0) describes a gym nobody built");
    for (let level = 1; level <= gym.levels.length; level++) {
      const t = trainingFor(level);
      if (t === null) {
        problems.push(`trainingFor(${level}) is null, but the gym has that level`);
        continue;
      }
      if (!(t.gainPoints > 0) || !Number.isFinite(t.gainPoints)) {
        problems.push(`the gym at level ${level} pays ${t.gainPoints} points`);
      }
      if (!(t.cooldownSec > 0) || !Number.isFinite(t.cooldownSec)) {
        problems.push(`the gym at level ${level} has cooldownSec ${t.cooldownSec} (must be > 0)`);
      }
    }
  }

  for (const skill of TRAINABLE_SKILLS) {
    if (!isTrainableSkill(skill)) problems.push(`the gym teaches "${skill}", which it refuses`);
  }

  const seenCrafts = new Set<string>();
  for (const c of HIDEOUT_CRAFTS) {
    const where = `craft "${c.id}"`;
    if (c.id.length === 0) problems.push("a craft has an empty id");
    if (seenCrafts.has(c.id)) problems.push(`duplicate craft id "${c.id}"`);
    seenCrafts.add(c.id);
    if (CRAFT_BY_ID.get(c.id) !== c) problems.push(`${where} is not the one the registry holds`);

    const mod = getHideoutModule(c.moduleId);
    if (mod === null) {
      problems.push(`${where} runs in unknown module "${c.moduleId}"`);
    } else if (!Number.isInteger(c.minLevel) || c.minLevel < 1 || c.minLevel > mod.levels.length) {
      problems.push(
        `${where} wants "${c.moduleId}" level ${c.minLevel}, which tops out at ${mod.levels.length}`,
      );
    }

    if (!(c.craftSec > 0) || !Number.isFinite(c.craftSec)) {
      problems.push(`${where} has craftSec ${c.craftSec} (must be > 0)`);
    }
    if (c.inputs.length === 0) problems.push(`${where} consumes nothing`);
    checkCosts(problems, where, c.inputs);
    // Output count is NOT checked against `stackMax`: two non-stacking bandages
    // is a real recipe, and collection places instances.
    checkCosts(problems, `${where} output`, [c.output]);
  }

  return problems;
}
